<template>
  <div class="mt-2 ml-2 mr-2">
    <div v-if="isManager">
      <b-row>
        <b-col>
          <b-card class="mb-2" border-variant="light" bg-variant="light">
            <p>Welcome to the SciStarter Integration!</p>
            <b-row>
              <b-col>
                <dl>
                  <dt><b>What is it?</b></dt>
                  <dd>
                    Connect your CitSci project to
                    <a href="https://scistarter.com" target="_blank" rel="noopener">SciStarter</a> <i
                    class="fas fa-external-link-alt fa-xs"></i>,
                    one of the world's largest citizen science platforms! The integration has two parts:
                    <ul>
                      <li><b>Project Integration:</b> Share your project's information with SciStarter so more people
                        can discover and join your research.
                      </li>
                      <li><b>Participation Integration:</b> Optionally share information about the number and frequency
                        of observations made by your volunteers, helping SciStarter recognize their contributions to
                        science.
                      </li>
                    </ul>
                  </dd>
                  <dt><b>Why should I use it?</b></dt>
                  <dd>
                    SciStarter helps connect citizen science projects with volunteers through schools, libraries, media
                    outlets, and partner platforms
                    like <a href="https://sciencenearme.org/" target="_blank" rel="noopener">Science Near Me</a> <i
                    class="fas fa-external-link-alt fa-xs"></i>.
                    Integrating your project can expand your reach, attract new participants, and help document your
                    project's impact.
                    SciStarter also offers volunteer recruitment tools, promotional opportunities, and participation
                    insights.
                  </dd>
                  <dt><b>How does Project Integration work?</b></dt>
                  <dd>
                    Once your CitSci project is published, enable the SciStarter Project Integration toggle to
                    automatically send your project information to SciStarter.
                    SciStarter's editorial team reviews submissions before publishing them. You may receive an email if
                    additional information is needed.
                    Once your project is approved, you'll receive an email from SciStarter about linking your SciStarter
                    account to your project listing.
                    If you don't already have a SciStarter account, you can create one using the same email address
                    associated with your CitSci project.
                    SciStarter can then help link your account to your project listing, giving you access to available
                    project management tools, volunteer recruitment features, and participation statistics.
                    For the best chance of approval, make sure your project contributes to scientific research, involves
                    public participation, and includes a clear title, goals, participation instructions, and relevant
                    images.
                    Updates to your project's information on CitSci will automatically be shared with SciStarter.
                  </dd>
                  <dt><b>What information is shared with SciStarter?</b></dt>
                  <dd>
                    CitSci shares your project's name, ID, description, goals, tasks, hub name, Facebook link (if
                    provided), banner image or logo, and the project owner's name and email address.
                    SciStarter uses this information to review, display, and maintain your project listing, contact you
                    about your submission, and help connect the project owner's SciStarter account to your project.
                  </dd>
                  <dt><b>What is the Participation Integration</b></dt>
                  <dd>
                    The Participation Integration is <b>separate and optional</b> and allows CitSci to share
                    participation activity with SciStarter.
                    When enabled, CitSci shares information about observations made by your project members. This helps
                    SciStarter recognize the number and frequency of contributions to your research.<br>
                    For each reported observation, CitSci shares:<br>
                    <ul class="mb-0">
                      <li>The participation activity type ('collection')</li>
                      <li>The date the observation was made ('observed at')</li>
                      <li>The hash of the project member's email (not their actual email address)</li>
                    </ul>
                    <b>Observation location data is not currently shared.</b><br>
                    The Participation Integration can only be turned On if the Project Integration is On.
                  </dd>
                  <dt><b>What if I want to stop sharing?</b></dt>
                  <dd>
                    You can change your integration preferences at any time.
                    <ul>
                      <li><b>Disable Project Integration:</b> CitSci sends SciStarter a request to hide your project
                        listing and stops sharing project updates. This will also turn off Participation Integration.
                      </li>
                      <li><b>Disable Participation Integration:</b> CitSci stops reporting new participation activity to
                        SciStarter. The Project Integration remains active.
                      </li>
                    </ul>
                  </dd>
                  Questions?
                  <b-link :to="{ name: 'Contact' }">Contact</b-link>
                  the CitSci team if you need assistance with the integration, linking your SciStarter account, or
                  managing your project listing.
                </dl>
              </b-col>
            </b-row>
            <b-row>
              <h6>Read more about the SciStarter API
                <a href="https://scistarter.org/api" target="_blank" rel="noopener">here</a>
                <span><i class="fas fa-external-link-alt fa-xs"></i></span>
              </h6>
            </b-row>
          </b-card>
          <b-card border-variant="light" bg-variant="light">
            <b-card-body>
              <b-row v-if="projectVuex.projectState" class="mb-3">
                <b-col>
                  <div class="mr-3"><b><u>Status:</u></b></div>
                  <div class="d-flex align-items-center gap-5">
                    <b-form-checkbox
                      v-model="projectVuex.isSciStarter"
                      :value=true
                      :unchecked-value=false
                      switch
                      size="lg"
                      class="mr-4">
                      {{
                        projectVuex.isSciStarter ? 'Your SciStarter Integration is turned ON' : 'Your SciStarter Integration is turned OFF'
                      }}
                    </b-form-checkbox>
                  </div>
                </b-col>
                <b-col>
                  <div class="mr-3"><b><u>Status:</u></b></div>
                  <div class="d-flex align-items-center gap-5">
                    <b-form-checkbox
                      v-model="projectVuex.isSciStarterParticipation"
                      :value=true
                      :unchecked-value=false
                      :disabled="!enableParticipation"
                      switch
                      size="lg">
                      {{
                        projectVuex.isSciStarterParticipation ? 'SciStarter Participation is turned ON' : 'SciStarter Participation is turned OFF'
                      }}
                    </b-form-checkbox>
                  </div>
                </b-col>
              </b-row>
              <b-row v-if="!projectVuex.projectState" class="mb-3">
                <b-col>
                  <div>
                    Your project needs to be Published before the SciStarter integration can be turned ON.<br>
                    Publish your project under
                    <b-link :to="{ name: 'ProjectEdit', params: { id: $route.params.id }}">Edit Project</b-link>
                  </div>
                </b-col>
              </b-row>
              <b-row v-if="projectVuex.projectState" class="mt-3">
                <b-col>
                  <b-link v-if="projectVuex && projectVuex.sciStarterID"
                          :href="scistarterURL" target="_blank">
                    <b-img
                      src="/assets/images/scistarter-affiliate-1200x332.png"
                      class="SciStarterLogoLink"
                      alt="Find us on SciStarter"
                      title="Click to view your project on SciStarter"
                      width="200"
                      fluid
                    >
                    </b-img>
                    <br>
                    {{ scistarterURL }} <span><i class="fas fa-external-link-alt fa-xs"></i></span>
                  </b-link>
                </b-col>
              </b-row>
            </b-card-body>
          </b-card>
          <SyncHistory v-show="false"></SyncHistory>
        </b-col>
      </b-row>
    </div>
    <RequiresManager v-else page="SciStarter Integration"></RequiresManager>
  </div>
</template>

<script>
import {mapActions, mapGetters} from "vuex";
import RequiresManager from "../tabpermission/requiresManager.vue";
import SyncHistory from "./SyncHistory.vue";

const FORUM_TAG = "SciStarter";
const FORUM_TITLE = "SciStarter Integration - Participation";

export default {
  components: {RequiresManager, SyncHistory},

  data() {
    return {
      collapse: true,
      isParticipationSaving: false,
      enableParticipation: false,
      revertingToggle: null,
    }
  },

  mounted() {
    if (this.projectVuex) {
      this.isSciStarter = this.projectVuex.isScistarter;
      this.isScistarterParticipation = this.projectVuex.isScistarterParticipation;
      this.enableParticipation = this.projectVuex.isSciStarter;
    }
  },

  watch: {
    'projectVuex.isSciStarter'(newVal, oldVal) {
      if (oldVal === undefined) {
        return;
      }
      if (this.revertingToggle === 'isSciStarter') {
        this.revertingToggle = null;
        return;
      }
      let wasParticipationOn = this.projectVuex.isSciStarterParticipation;

      // If the integration status button is changed, we want to disable the
      // participation button to avoid a race condition until the user has
      // confirmed and the server update has been done.
      this.enableParticipation = false;

      const getPostsPromise = (!this.discussionForumData || this.discussionForumData.length === 0)
        ? this.getDiscussionForumData(this.projectVuex.id)
        : Promise.resolve(this.discussionForumData);
      getPostsPromise.then((posts) => {
        let post = this.discussionForumData.length > 0
          ? this.discussionForumData.find(item => item.subject === FORUM_TITLE)
          : null;

        return this.displayMessage(newVal, post, "integration", wasParticipationOn);
      }).then((confirmed) => {
        if (!confirmed) {
          this.revertToggle('isSciStarter', oldVal);
          return;
        }
        return this.editProject({isSciStarter: this.projectVuex.isSciStarter}).then(data => {
          //this.$root.$emit('refreshHistory', true);
        });
      }).catch((e) => {
        console.error("getPostsPromise error:", e);
      }).finally(() => {
        this.enableParticipation = this.projectVuex.isSciStarter;
      });
    },

    'projectVuex.isSciStarterParticipation'(newVal, oldVal) {

      if (oldVal === undefined) return;
      if (this.revertingToggle === 'isSciStarterParticipation') {
        this.revertingToggle = null;
        return;
      }
      if (this.isParticipationSaving) return;

      this.isParticipationSaving = true;

      let cont = true;
      const getPostsPromise = (!this.discussionForumData || this.discussionForumData.length === 0)
        ? this.getDiscussionForumData(this.projectVuex.id)
        : Promise.resolve(this.discussionForumData);
      getPostsPromise.then((posts) => {
        let post = this.discussionForumData.length > 0
          ? this.discussionForumData.find(item => item.subject === FORUM_TITLE)
          : null;

        // If turning off participation and there is no post, then just skip
        // the rest.  This is the case for the few projects that we've manually
        // turned participation on in the database
        if (newVal === false && !post)
          cont = false;

        const shouldDisplay = cont && (newVal !== false || this.projectVuex.isSciStarter !== false);
        const confirmPromise = shouldDisplay
          ? this.displayMessage(newVal, post, "participation")
          : Promise.resolve(true);

        return confirmPromise.then((confirmed) => {
          if (!confirmed) {
            this.revertToggle('isSciStarterParticipation', oldVal);
            return;
          }
          if (cont) {
            this.handleForumPost(newVal, post);
          }
          return this.editProject({isSciStarterParticipation: this.projectVuex.isSciStarterParticipation}).then(data => {
            //this.$root.$emit('refreshHistory', true);
          });
        });
      }).catch((e) => {
        console.error("getPostsPromise error:", e);
      }).finally(() => {
        this.isParticipationSaving = false;  // release the guard
      });
    },
  },

  computed: {
    ...mapGetters({
      isManager: "project/get/isManager",
      projectVuex: "project/get/project",
      discussionForumData: "project/show/discussionForumData",
    }),

    scistarterURL() {
      const urlArray = this.projectVuex.urlField.split("/");
      return `https://www.scistarter.org/${urlArray[urlArray.length - 1]}`;
    },
  },

  methods: {
    ...mapActions({
      editProject: "project/update/editProject",
      getDiscussionForumData: "project/show/getDiscussionForumData",
      addReplyToDiscussion: "project/forum/create",
      startNewDiscussion: "project/forum/create",
    }),

    // whichOption parameter should be either "integration" or "participation"
    // since it will be used in the message...
    // Returns the msgBoxConfirm promise, which resolves true only on YES.
    displayMessage(newVal, post, whichOption, wasParticipationOn = false) {
      let lines = [];
      let mode = newVal ? "ON" : "OFF";

      if (whichOption === "integration") {
        lines = ["All managers will be notified via email."];
        if (mode === "ON") {
          lines.push("Turning on this integration will send your project profile to SciStarter's publicly searchable database.");
          lines.push("We recommend also turning on the SciStarter participation option, so project observations will automatically be sent to SciStarter for members who have an account on that platform.");
        } else if (wasParticipationOn === true) {
          lines.push("We will also turn off the participation option for your project, meaning that we will no longer send the number and frequency of user contributions to your project to SciStarter.");
        }
      } else if (whichOption === "participation") {
        if (post) {
          let forumURL = "/projects/" + this.projectVuex.urlField + "/forum/" + post.id;
          lines.push(this.$createElement('span',
            ['A reply with the new status will be added to the ',
              this.$createElement('a', {
                  attrs: {
                    href: forumURL,
                    target: '_blank', rel: 'noopener noreferrer'
                  }
                },
                'existing forum post.')]));

        } else if (mode) {
          lines.push("A new forum post will be created for this project.");
          lines.push("All project members will be notified via email.");
        }
      }

      const ulElement = this.$createElement('ul', {
        style: {textAlign: 'left', paddingLeft: '20px'}
      }, lines.map(text => this.$createElement('li',
        typeof text === 'string' ? text : [text])));

      return this.$bvModal.msgBoxConfirm(ulElement, {
        title: "Turn " + mode + " SciStarter " + whichOption + "?",
        size: "md",
        buttonSize: "sm",
        okVariant: "danger",
        okTitle: "YES",
        cancelTitle: "NO",
        footerClass: "p-2",
        hideHeaderClose: false,
        centered: true,
      });
    },

    // Puts a toggle back after the user answers NO, without re-running its watcher.
    revertToggle(field, value) {
      this.revertingToggle = field;
      this.projectVuex[field] = value;
    },

    handleForumPost(newVal, post) {
      let mode = newVal ? "ON" : "OFF";

      if (post) {
        this.addReplyToDiscussion({
          parentPost: `/project_posts/${post.id}`,
          discussionText: "SciStarter's participation integration is now <b>" + mode + "</b>.",
        })
      } else {
        let body = "<p>This project is now sharing the date and time of observations with SciStarter.\n" +
          "Any updates to this sharing will be added as replies to this post." +
          "Subscribe to this forum post to receive email updates about changes." +
          "</p>" +
          "<p>As a member of this project, you can get credit for your observations if you have an account on SciStarter with the same email address." +
          "Your observation stats will show up on <a href='https://scistarter.org/dashboard' target='_blank' rel='noopener'>your personal SciStarter dashboard</a>.</p>" +
          "<p>Click to <a href='https://scistarter.org/login' target='_blank' rel='noopener'>Create a SciStarter account</a>.</p>";

        this.startNewDiscussion({
          subject: FORUM_TITLE,
          discussionText: body,
          isEmailMembers: true,
          isHidden: false,
          tags: [FORUM_TAG],
        });
      }
    },
  },
};

</script>

<style lang="scss" scoped>
</style>
